import { Button, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { useEffect, useState } from "react";

import { useGetTeamSubscription } from "../../api/generated/endpoints/billing/billing";
import { ErrorAlert } from "../../platform/ErrorAlert";
import type { PaidContinuation } from "./plans-types";

export const CHECKOUT_POLL_MS = 2_000;
export const CHECKOUT_WAIT_MS = 60_000;

export interface CheckoutReturnProps extends PaidContinuation {
  result: "success" | "canceled";
  onChoosePlan: () => void;
}

/** A Stripe redirect is navigation, never evidence of payment or a first-ad clean-copy entitlement. */
export function CheckoutReturn({ teamId, result, onPaidContinue, onChoosePlan, onContinueToApp }: CheckoutReturnProps) {
  const [attempt, setAttempt] = useState(0);
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setExpired(true), CHECKOUT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [attempt]);
  const query = useGetTeamSubscription(teamId, { query: {
    staleTime: 0, refetchOnMount: "always", retry: false,
    refetchInterval: (current) => result === "success" && !expired && current.state.status !== "error"
      && current.state.data?.subscription?.status !== "active" ? CHECKOUT_POLL_MS : false,
  } });
  const confirmed = query.isSuccess && query.isFetchedAfterMount && !query.isFetching
    && query.data.subscription?.status === "active";
  const retry = () => { setExpired(false); setAttempt((value) => value + 1); void query.refetch(); };
  return <Stack gap="lg" align="center" aria-live="polite">
    {onContinueToApp ? <Button variant="subtle" onClick={() => onContinueToApp()}>Continue to app</Button> : null}
    <Title order={1}>{confirmed ? "Your plan is active." : result === "canceled" ? "Checkout canceled" : "Confirming your plan"}</Title>
    {query.isError ? <ErrorAlert error={query.error} /> : null}
    {confirmed ? <>
      <Text>You can keep creating ads on your plan.</Text>
      <Button onClick={() => onPaidContinue()}>Make another ad</Button>
    </> : <>
      {query.isFetching ? <Loader aria-label="Checking subscription" /> : null}
      <Text>{result === "canceled" ? "Your Checkout was canceled. You can choose a plan again."
        : expired ? "Your plan is not confirmed yet. Check again in a moment."
        : "Waiting for payment confirmation. This can take a moment."}</Text>
      <Group>
        <Button variant="default" onClick={retry} loading={query.isFetching}>Check again</Button>
        <Button variant="subtle" onClick={onChoosePlan}>Choose a plan</Button>
      </Group>
    </>}
  </Stack>;
}
